import { useApolloClient } from '@apollo/client';
import {
  CommonActions,
  NavigationContainer,
  useNavigationState,
  getFocusedRouteNameFromRoute,
} from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { Formik } from 'formik';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Dimensions, Pressable } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { DropZone } from '@components/DropZone';
import { TeamsModal } from '@components/FormModals/TeamsModal';
import {
  EditTeamConfirmModal,
  TrialConfirmModal,
  UpgradeConfirmModal,
} from '@components/Modals/AnnualConfirmModal.web';
import { TeamPaywallModal } from '@components/Modals/TeamPaywallModal.web';
import { WebModal } from '@components/Modals/WebModal.web';
import NewEditProfile from '@components/Profile/NewEditProfile.web';
import {
  DeferredInvitation,
  InviteMemberModal,
} from '@components/Profile/TeamDetailPage/InviteMemberModal.web';
import { ProjectChecklistTemplate } from '@components/ProjectDetail/ProjectChecklist/ProjectChecklistTemplate';
import { ProjectSaveAsChecklistTemplate } from '@components/ProjectDetail/ProjectChecklist/ProjectSaveAsChecklistTemplate.web';
import CreateProject from '@components/Projects/CreateProject';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import { TaskWebPanel } from '@components/Tasks/TaskWebPanel';
import { Overlay } from '@components/Tutorialization/Web/OverlayWeb';
import {
  ChatEmptyState,
  EmptyStateType,
} from '@components/Web/Chats/ChatEmptyState.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  fadeOutThenAction,
  onboardingChipShadow,
  onboardingLayerStyle,
  ONBOARDING_FADE_IN_MS,
  ONBOARDING_STACK_PEEK,
} from '@components/Web/Navigator/onboardingStack';
import {
  SlideBackdropBox,
  SlidePanelBox,
  SlidePanelPresence,
} from '@components/Web/Navigator/SlideInPanel.web';
import {
  GetTeamBillingDocument,
  GetTeamDetailDocument,
  ListTeamsDocument,
  LocalFile,
  Project,
  Task,
  Team,
  TeamMemberRole,
  User,
  useCreateStripeSubscriptionMutation,
  useEditStripeSubscriptionMutation,
  useListProductsQuery,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useFileProcessor from '@hooks/useFileProcessor';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import { useOpenOwnerChatAfterLogin } from '@hooks/useOpenOwnerChatAfterLogin.web';
import { useProjectOnboarding } from '@hooks/useProjectOnboarding';
import { useProjectTutorialWeb } from '@hooks/useProjectTutorialWeb';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import CreateSubscriptionStack from '@navigation/payments/create-subscription-stack';
import { FormValues } from '@navigation/teams/create-team-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { BillingCycle } from '@root/types';
import { createTeamSchemaPaywall } from '@schemas/createTeamSchema';
import CreateContactStack from '@screens/Contacts/CreateContactStack.web';
import { SubscriptionCreatedModal } from '@screens/Payments/SubscriptionCreateModal';
import { ProjectOnboardingCard } from '@screens/Projects/ProjectOnboardingCard';
import { WEB_PLAN_NAME } from '@src/constants/constants';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { buildDeferredTeamSubscriptionVariables } from '@utils/teams/deferredTeamSubscription';
import {
  buildPendingTeamValues,
  clearPendingTeamSignup,
  isPendingTeamSignupForUser,
  readPendingTeamSignup,
} from '@utils/teams/pendingTeamSignup';
import {
  resolvePaywallSeatCount,
  resolveTeamSeatCount,
} from '@utils/teams/teamSeatCount';
import { uploadTeamLogo } from '@utils/teams/uploadTeamLogo';

const selectProjectDetailTabName = (state: any) => {
  if (!state) return null;
  const projectsStackRoute = state.routes.find(
    (route: any) => route.name === 'projects-stack'
  );
  if (!projectsStackRoute?.state) return null;
  const detailsRoute = projectsStackRoute.state.routes.find(
    (route: any) => route.name === 'details'
  );
  if (!detailsRoute?.state) return null;
  return getFocusedRouteNameFromRoute(detailsRoute) ?? null;
};

export const MainPanel = ({ children }: { children: React.ReactNode }) => {
  const {
    chatExpanded,
    chatListOpen,
    isBottomBannerShow,
    setIsBottomBannerShow,
    isCreateProjectOpen,
    setIsCreateProjectOpen,
    editProjectUUId,
    setEditProjectUUId,
    isTaskWebPanelOpen,
    setIsTaskWebPanelOpen,
    idTaskDetailOpen,
    setIdTaskDetailOpen,
    isTaskWebPanelMaximize,
    mainPanelWidth,
    isUpgradeTeamPlanOpen,
    setUpgradeTeamPlanOpen,
    idTeamOpen,
    setIdTeamOpen,
    uuidTeamOpen,
    setUUIdTeamOpen,
    teamValues,
    setTeamValues,
    isTeamPersonal,
    upgradeCTA,
    setUpgradeCTA,
    idContactDetailOpen,
    isContactWebPanelOpen,
    isEditProfileOpen,
    setIsEditProfileOpen,
    //web project checklist -> use template/Add Item from Template ->
    // '' close, '-1': close & has saved item, others: open
    idProjectChecklistTemplateOpen,
    setIdProjectChecklistTemplateOpen,
    //web project checklist -> Save Checklist as Template ->
    // '' close, '-1': close & has created template
    // , '-2': close & has updated template item, others: open
    idProjectSaveChecklistAsTemplateOpen,
    setIdProjectSaveChecklistAsTemplateOpen,
    subscriptionCreatedModalOpen,
    setSubscriptionCreatedModalOpen,
    subscriptionCreatedTeamUUId,
    setSubscriptionCreatedTeamUUId,
    upgradeConfirmModalOpen,
    setUpgradeConfirmModalOpen,
    editTeamConfirmModalOpen,
    setEditTeamConfirmModalOpen,
    editTeamConfirmTeam,
    onDropFile,
    paywallModalOpen,
    setPaywallModalOpen,
    paywallTeam,
    setPaywallTeam,
    showPaywallTeamSetup,
    setShowPaywallTeamSetup,
    showPaywallInviteMembers,
    setShowPaywallInviteMembers,
    showPaywallSubscriptionModal,
    setShowPaywallSubscriptionModal,
    setIsNewTeam,
    setIsTeamPersonal,
  } = useWebDrawer();

  const { drawerCollapsed } = useActiveChat();
  const { me, meNetworkReady } = useMe();

  const { showOverlay, setShowOverlay } = useProjectTutorialWeb();

  // TA-10949 #24: the create/edit-project panel's X lives outside
  // ProjectForm.web's Formik tree and used to close unconditionally.
  // ProjectForm registers a "safe to close?" function here; when it hasn't
  // (not yet mounted), fall back to closing immediately.
  const createProjectAttemptCloseRef = useRef<(() => void) | null>(null);
  const handleCreateProjectHeaderClose = () => {
    if (createProjectAttemptCloseRef.current) {
      createProjectAttemptCloseRef.current();
    } else {
      setIsCreateProjectOpen(false);
      setEditProjectUUId('');
      setShowOverlay(false);
    }
  };

  const apolloClient = useApolloClient();
  const {
    currentProject,
    visibleProjects,
    hiddenCount,
    dismissProject,
    dismissAll,
    goToProject,
  } = useProjectOnboarding();

  // Everything queued behind the card the user is looking at — the slivers
  // rendered under it plus the ones past MAX_VISIBLE_ONBOARDING_CARDS. Drives
  // both the "+N more, behind this one" label and the dismiss-all control.
  const behindCount = Math.max(visibleProjects.length - 1, 0) + hiddenCount;
  useOpenOwnerChatAfterLogin(currentProject?.id);

  const isMobileBrowser = React.useMemo(() => {
    if (globalThis.window === undefined) return false;
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      globalThis.window.navigator.userAgent
    );
  }, []);
  const cardFadeAnim = useRef(new Animated.Value(0)).current;
  // Capture the deep-link UUID at render time before React Navigation can
  // replace window.location via history.replaceState in its own effects.
  const choosePlanDeepLinkUUId = useRef<string | null>(
    typeof window !== 'undefined'
      ? (() => {
          const m = window.location.pathname.match(
            /^\/my-profile\/([^/?#]+)\/choose-plan/
          );
          return m ? m[1] : null;
        })()
      : null
  );

  // Fades the whole stack in when it first appears. Keyed on presence rather
  // than on the top project's id so dismissing one card of a multi-card stack
  // doesn't re-run the fade (which would blink the cards left behind).
  useEffect(() => {
    if (!currentProject) return;
    cardFadeAnim.setValue(0);
    Animated.timing(cardFadeAnim, {
      toValue: 1,
      duration: ONBOARDING_FADE_IN_MS,
      useNativeDriver: true,
    }).start();
  }, [!!currentProject]);

  // Handle newly_joined_task_id from sessionStorage — opens task panel after task invitation signup
  // This is similar to how notification_chat_id is handled in WebDrawerNavigator
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const joinedTaskId = window.sessionStorage.getItem('newly_joined_task_id');
    window.sessionStorage.removeItem('newly_joined_task_id');
    if (joinedTaskId) {
      setIdTaskDetailOpen(joinedTaskId);
      setIsTaskWebPanelOpen(true);
    }
  }, []);

  const fadeOutThen = useCallback(
    (action: () => void) => fadeOutThenAction(cardFadeAnim, action),
    [cardFadeAnim]
  );
  const [isModalShow, setIsModalShow] = useState(false);
  const [, setConfirmEditLoading] = useState(false);
  const { processFiles } = useFileProcessor();
  const { t } = useTranslation();
  const navigation = useAppNavigation();
  const { isOpenChat } = useActiveChat();
  const chatWidth = Dimensions.get('window').width <= 1280 ? 480 : 550;
  const toast = useToast();
  const continuingToCheckoutRef = useRef(false);

  // Track the current route name using navigation state
  // This will automatically update when navigation state changes
  const currentRouteName = useNavigationState(selectProjectDetailTabName);

  // getPublicTeam, not getTeam (TA-10850): getTeam answers "The requested team
  // could not be found" for a viewer who is not a member of the team yet.
  const { fullTeam: team } = useTeamDetailFromQuery({
    teamUUId: uuidTeamOpen,
    includePending: true,
    // TA-10847: only a member count is needed here (usersCount below), not
    // the full users list. Matches DrawerHeader's variables.
    withMembers: false,
  });

  const [editStripeSubscription, { error: editStripeSubscriptionError }] =
    useEditStripeSubscriptionMutation();

  useEffect(() => {
    if (!editStripeSubscriptionError) return;
    setConfirmEditLoading(false);
    toast.show('Failed to update subscription. Please try again.', {
      type: 'danger',
      duration: 3000,
    });
  }, [editStripeSubscriptionError]);

  useEffect(() => {
    if (!upgradeConfirmModalOpen && !isUpgradeTeamPlanOpen) {
      setUUIdTeamOpen('');
      setIdTeamOpen('');
      setTeamValues(undefined);
    }
    // When CreateSubscription closes, also dismiss the paywall subscription modal
    if (!isUpgradeTeamPlanOpen && showPaywallSubscriptionModal) {
      setShowPaywallSubscriptionModal(false);
    }
  }, [
    upgradeConfirmModalOpen,
    isUpgradeTeamPlanOpen,
    showPaywallSubscriptionModal,
  ]);

  // TA-10886: a team named during web registration is not created at signup
  // anymore. Pick the stored values up once the app stack mounts and enter
  // the same Stripe-first checkout the Team page uses, so the card is
  // collected first and abandoning the flow creates nothing. (Checkout itself
  // still calls createTeam just before taking payment — see
  // CreateSubscription.submitForm — so a payment failure after that point can
  // still leave a team behind. That is shared with every TA-10009 entry point
  // and is tracked separately.)
  useEffect(() => {
    if (!me) return;
    const pendingTeam = readPendingTeamSignup();
    if (!pendingTeam) return;
    // useMe is cache-and-network, so `me` can first be the persisted snapshot
    // of the account previously signed in to this tab. Matching the payload's
    // own signup address means such a snapshot is skipped (not consumed and
    // cleared) and the real user still gets checkout when GetMe resolves.
    if (!isPendingTeamSignupForUser(pendingTeam, me.email)) return;
    // A payload with no ownerEmail (written by an older build still open in
    // this tab) matches everyone, so it is the one case where identity has to
    // come from the network before it is consumed and cleared.
    if (!pendingTeam.ownerEmail && !meNetworkReady) return;
    clearPendingTeamSignup();
    setIsTeamPersonal(false);
    setTeamValues(
      buildPendingTeamValues(pendingTeam, {
        id: me.id,
        name: me.name,
        firstName: me.firstName,
        lastName: me.lastName,
        avatar: me.avatar,
        role: TeamMemberRole.Owner,
      })
    );
    setUpgradeConfirmModalOpen(true);
  }, [me?.id, me?.email, meNetworkReady]);

  // ── Paywall trial flow: Team Setup → Invite Members → TrialConfirmModal ──

  // TA-9875: cache-first — MainPanel never unmounts, so route changes should
  // not fire redundant network requests. Cache is currently kept fresh by
  // refetchQueries on team mutations; TA-9877 will replace those with Apollo
  // cache direct-writes.
  const { teams: allTeams } = useListTeamsFromQuery({
    includePending: true,
    fetchPolicy: 'cache-first',
  });
  // Open the upgrade modal when the user arrives via the email CTA deep link
  // (/my-profile/{uuid}/choose-plan). React Navigation doesn't match that path
  // (it expects my-account/my-profile/…), so we handle it here instead.
  useEffect(() => {
    const uuid = choosePlanDeepLinkUUId.current;
    if (!uuid || !allTeams.length) return;
    const team = allTeams.find((t) => t.uuid === uuid);
    if (!team) return;
    choosePlanDeepLinkUUId.current = null;
    setTeamValues(team);
    setIdTeamOpen(team.id ?? '');
    setUUIdTeamOpen(team.uuid ?? '');
    setUpgradeConfirmModalOpen(true);
    navigation.dispatch(CommonActions.navigate({ name: 'projects-stack' }));
  }, [allTeams]);
  const teamSetupInitialValues: FormValues = {
    name: '',
    skillIds: [],
    logo: null,
    location: null,
  };

  // New teams are no longer created here — the backend creates the team from
  // teamAttributes when createStripeSubscription runs after the trial/checkout
  // step, so abandoning the flow leaves no orphaned team behind.
  const [paywallPendingMembers, setPaywallPendingMembers] = useState<
    DeferredInvitation[]
  >([]);
  // One count for the trial-confirmation modal and the subscription it creates
  // (TA-10847 QA): usersCount first, never a possibly stale users array.
  const paywallSeatCount = resolvePaywallSeatCount({
    paywallTeam,
    team,
    pendingMemberCount: paywallPendingMembers.length,
  });

  const submitPaywallTeamSetup = (
    values: FormValues,
    setSubmitting: (a: boolean) => void
  ) => {
    setSubmitting(true);
    const { name } = values;

    setShowPaywallTeamSetup(false);
    setTeamValues(values);

    if (name) {
      // Deferred-creation path: keep the team ids empty until the
      // subscription mutation returns the created team.
      setIdTeamOpen('');
      setUUIdTeamOpen('');
      setPaywallTeam(undefined);
      setIsNewTeam(true);
      setIsTeamPersonal(false);
    } else {
      if (paywallTeam?.id && paywallTeam?.uuid) {
        setIdTeamOpen(paywallTeam.id);
        setUUIdTeamOpen(paywallTeam.uuid);
      } else {
        setIdTeamOpen('');
        setUUIdTeamOpen('');
      }
      setIsNewTeam(false);
      setIsTeamPersonal(false);
    }
    setPaywallPendingMembers([]);
    setShowPaywallInviteMembers(true);
    setSubmitting(false);
  };

  const handlePaywallInviteDone = useCallback(() => {
    setShowPaywallInviteMembers(false);
    setShowPaywallSubscriptionModal(true);
    setUpgradeTeamPlanOpen(true);
  }, []);

  const { data: listProductsData } = useListProductsQuery();
  const listProducts = listProductsData?.listProducts;
  const teamPlan = listProducts?.find((p) =>
    p?.name?.toLowerCase()?.includes(WEB_PLAN_NAME)
  );
  const yearlyPrice = teamPlan?.prices?.find(
    (price) => price?.interval === 'year'
  );
  const monthlyPrice = teamPlan?.prices?.find(
    (price) => price?.interval === 'month'
  );

  const [createStripeSubscription] = useCreateStripeSubscriptionMutation();

  const handlePaywallStartTrial = useCallback(
    async (billingCycle: BillingCycle) => {
      const isYearly =
        (billingCycle as string) === 'year' ||
        (billingCycle as string) === 'annually';
      const selectedPrice = isYearly ? yearlyPrice : monthlyPrice;
      if (!teamPlan || !selectedPrice?.externalId) return;

      const teamIdForTrial = idTeamOpen || paywallTeam?.id;
      const pendingValues = teamValues as FormValues | undefined;
      // Deferred-creation path: no team exists yet, the subscription mutation
      // creates it from teamAttributes.
      const isDeferredNewTeam = !teamIdForTrial;
      if (isDeferredNewTeam && !pendingValues?.name) return;

      let treatAsSuccess = false;
      let subscribedTeamUUId = uuidTeamOpen || paywallTeam?.uuid || '';

      try {
        if (isDeferredNewTeam) {
          const avatarBlobId = await uploadTeamLogo(
            pendingValues?.logo as LocalFile | null,
            processFiles
          );
          const { data } = await createStripeSubscription({
            variables: buildDeferredTeamSubscriptionVariables({
              values: pendingValues ?? {},
              avatarBlobId,
              pendingMembers: paywallPendingMembers,
              planId: teamPlan.id,
              priceId: selectedPrice.externalId,
            }),
            refetchQueries: [
              { query: ListTeamsDocument, variables: { includePending: true } },
              {
                query: GetTeamBillingDocument,
                variables: { includePending: true },
              },
            ],
          });
          subscribedTeamUUId =
            data?.createStripeSubscription?.team?.uuid ?? subscribedTeamUUId;
        } else {
          await createStripeSubscription({
            variables: {
              stripeSubscriptionAttributes: {
                name: teamPlan.id,
                teamId: teamIdForTrial,
                quantity: paywallSeatCount,
                priceId: selectedPrice.externalId,
              },
            },
          });
        }

        treatAsSuccess = true;
      } catch (error: any) {
        const errorMsg = String(error?.message || error || '');
        const isApolloCacheError =
          errorMsg.includes('Missing field') ||
          errorMsg.includes('writeToStore') ||
          errorMsg.includes('while writing result');

        if (isApolloCacheError) {
          treatAsSuccess = true;
        } else {
          Sentry.captureException(error);
          toast.show('Failed to start trial. Please try again.', {
            type: 'danger',
            duration: 3000,
          });
        }
      } finally {
        if (treatAsSuccess) {
          setUpgradeTeamPlanOpen(false);
          setShowPaywallSubscriptionModal(false);
          setPaywallPendingMembers([]);
          setSubscriptionCreatedTeamUUId(subscribedTeamUUId);
          setSubscriptionCreatedModalOpen(true);
        }
      }
    },
    [
      teamPlan,
      yearlyPrice,
      monthlyPrice,
      idTeamOpen,
      paywallSeatCount,
      paywallTeam,
      teamValues,
      paywallPendingMembers,
      processFiles,
      createStripeSubscription,
      uuidTeamOpen,
      setUpgradeTeamPlanOpen,
      setShowPaywallSubscriptionModal,
      setSubscriptionCreatedTeamUUId,
      setSubscriptionCreatedModalOpen,
    ]
  );

  const handleDrop = useCallback(
    async (files: File[]) => {
      if (onDropFile && typeof onDropFile === 'function') {
        onDropFile(files);
      }
    },
    [onDropFile]
  );

  const handleContinueToCheckout = (billingCycle: BillingCycle) => {
    continuingToCheckoutRef.current = true;
    setTeamValues((prev: any) => ({
      ...prev,
      billingCycle,
    }));
    setUpgradeConfirmModalOpen(false);
    setUpgradeCTA(true);
    setUpgradeTeamPlanOpen(true);
  };

  const handleConfirmEditTeam = async () => {
    const currentTeam = editTeamConfirmTeam || team;

    if (currentTeam?.subscriptionDetails?.subscriptionInterval === 'year') {
      setEditTeamConfirmModalOpen(false);
      toast.show('Team is already on annual billing', {
        duration: 2000,
      });
      return;
    }

    const annualPrice = currentTeam?.product?.prices?.find(
      (price) => price?.interval === 'year'
    );

    if (!annualPrice?.externalId || !currentTeam?.product?.name) {
      toast.show('Unable to find annual pricing. Please try again.', {
        type: 'danger',
        duration: 3000,
      });
      return;
    }

    let treatAsSuccess = false;

    try {
      setConfirmEditLoading(true);

      await editStripeSubscription({
        variables: {
          stripeSubscriptionAttributes: {
            name: currentTeam.product.name,
            teamId: currentTeam.id.toString(),
            quantity: currentTeam.usersCount || 1,
            priceId: annualPrice.externalId,
          },
        },
        refetchQueries: [
          {
            query: GetTeamDetailDocument,
            variables: { id: currentTeam.uuid, includePending: true },
          },
        ],
      });

      treatAsSuccess = true;
    } catch (error: any) {
      const errorMsg = String(error?.message || error || '');
      const isApolloCacheError =
        errorMsg.includes('Missing field') ||
        errorMsg.includes('writeToStore') ||
        errorMsg.includes('while writing result');

      if (isApolloCacheError) {
        treatAsSuccess = true;
      } else {
        toast.show('Failed to update subscription. Please try again.', {
          type: 'danger',
          duration: 3000,
        });
      }
    } finally {
      setConfirmEditLoading(false);
      setEditTeamConfirmModalOpen(false);

      if (treatAsSuccess) {
        toast.show(
          <Box
            flexDirection='row'
            alignItems='center'
            paddingHorizontal='l'
            paddingVertical='m'>
            <Icon name='Check' width={24} height={24} color='white' />
            <Text marginLeft='s' variant='heading2' color='white'>
              Plan updated to Yearly
            </Text>
          </Box>,
          {
            duration: 3000,
            placement: 'bottom',
            style: {
              backgroundColor: '#000',
              borderRadius: 8,
              paddingHorizontal: 0,
              paddingVertical: 0,
            },
          }
        );
      }
    }
  };

  const fallbackUpgradeTeamFromList = allTeams.find(
    (teamItem) =>
      (idTeamOpen && String(teamItem.id) === String(idTeamOpen)) ||
      (uuidTeamOpen && teamItem.uuid === uuidTeamOpen)
  );
  const teamValuesForUpgrade =
    teamValues && typeof teamValues === 'object' && 'id' in teamValues
      ? (teamValues as Team)
      : undefined;
  const upgradeConfirmTeam =
    team ?? fallbackUpgradeTeamFromList ?? teamValuesForUpgrade;
  // Deferred team creation (TA-10009): no team exists yet, so count the
  // locally collected owner/users plus pending e-mail invitations instead
  const deferredMemberCount =
    (teamValues?.users?.length ?? 1) + (teamValues?.teamMembers?.length ?? 0);
  const upgradeConfirmMemberCount = resolveTeamSeatCount(
    [upgradeConfirmTeam],
    deferredMemberCount
  );
  const upgradeConfirmTeamName =
    upgradeConfirmTeam?.name ??
    (teamValues && typeof teamValues === 'object' && 'name' in teamValues
      ? String(teamValues.name ?? '')
      : undefined);

  const leftPartWidth = drawerCollapsed ? 86 : 256;

  const panelContent = (isTeam: boolean) => {
    const panelMinWidth = 580;
    const width =
      !isTeam && isTaskWebPanelMaximize ? mainPanelWidth : panelMinWidth;
    const initialParams = isTeam
      ? {
          teamId: idTeamOpen,
          teamUUId: uuidTeamOpen,
          teamValues: teamValues,
          isPersonal: isTeamPersonal,
          isPlanPlus: false,
          screenName: 'create-subscriptionPanel',
        }
      : {};

    const taskContent = (
      <>
        {isProjectChecklistTemplateOpen && (
          <ProjectChecklistTemplate
            projectUUId={
              idProjectChecklistTemplateOpen
            }></ProjectChecklistTemplate>
        )}
        {isProjectSaveChecklistAsTemplateOpen && (
          <Formik initialValues={{ name: '' }}>
            {() => {
              return <ProjectSaveAsChecklistTemplate />;
            }}
          </Formik>
        )}
        {!isFromProjectChecklist && isTeam && (
          <CreateSubscriptionStack
            externalNavigate={navigation}
            params={initialParams}
          />
        )}
        {!isFromProjectChecklist && !isTeam && (
          <TaskWebPanel
            externalNavigate={navigation}
            idtask={idTaskDetailOpen}
            onNavigation={(task: Task) => {
              setIdTaskDetailOpen(task.id);
              setIsTaskWebPanelOpen(true);
              if (showOverlay) setShowOverlay(false);
            }}
            onPressCreator={(task: Task) => {
              isTaskWebPanelOpen && setIsTaskWebPanelOpen(false);
              navigation.navigateToContact(task.creator);
            }}
            onPressMember={(item: User) => {
              isTaskWebPanelOpen && setIsTaskWebPanelOpen(false);
              navigation.navigateToContact(item);
            }}
            onPressProject={(project: Project) => {
              isTaskWebPanelOpen && setIsTaskWebPanelOpen(false);
              navigation.navigateToProject(project);
            }}
            onNavigateToAccount={() => {
              setIsTaskWebPanelOpen(false);
              navigation.navigate('my-account');
            }}
            onNavigateToProjects={() => {
              setIsTaskWebPanelOpen(false);
              navigation.navigate('projects-stack');
            }}
          />
        )}
      </>
    );

    return (
      <NavigationContainer
        documentTitle={{ enabled: false }}
        independent={true}>
        {(isFromProjectChecklist || isTaskWebPanelOpen) && (
          <Pressable
            onPress={() => {
              // Dismiss only the topmost panel: a task panel under the
              // checklist template slides back in, as it does on the X.
              if (isFromProjectChecklist) {
                setIdProjectChecklistTemplateOpen('');
                setIdProjectSaveChecklistAsTemplateOpen('');
              } else if (isTaskWebPanelOpen) {
                setIsTaskWebPanelOpen(false);
              }
            }}
            // Full-width for every panel: the panel paints over the backdrop.
            // Stopping it short of the panel (the old Checklist Template
            // `right: panelMinWidth - 1`) left an undimmed strip that the
            // panel slid across, read as ghosting (TA-10891 QA).
            style={{
              position: 'absolute',
              left: -leftPartWidth,
              bottom: 0,
              top: 0,
              right: 0,
            }}>
            <SlideBackdropBox
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </Pressable>
        )}

        {showOverlay ? (
          <Overlay visible={showOverlay} animationType='slide-panel'>
            <SlidePanelBox
              accessibilityLabel='Task modal'
              style={{
                borderColor: theme.colors.black,
                borderStyle: 'solid',
                backgroundColor: theme.colors.white,
                elevation: 5,
                shadowColor: theme.colors.buttonBackground,
                shadowOpacity: 0.1,
                shadowRadius: 40,
              }}
              position='absolute'
              right={
                !chatExpanded && !(isOpenChat || chatListOpen) ? 80 : chatWidth
              }
              bottom={0}
              top={0}
              width={isFromProjectChecklist ? panelMinWidth : width}
              p={isFromProjectChecklist ? 'none' : 'l'}>
              {taskContent}
            </SlidePanelBox>
          </Overlay>
        ) : (
          <SlidePanelBox
            accessibilityLabel='Task modal'
            style={{
              borderColor: theme.colors.black,
              borderStyle: 'solid',
              backgroundColor: theme.colors.white,
              elevation: 5,
              shadowColor: theme.colors.buttonBackground,
              shadowOpacity: 0.1,
              shadowRadius: 40,
            }}
            position='absolute'
            right={0}
            bottom={0}
            top={0}
            width={isFromProjectChecklist ? panelMinWidth + 30 : width}
            p={noPadding ? 'none' : 'l'}>
            {taskContent}
          </SlidePanelBox>
        )}
      </NavigationContainer>
    );
  };
  const isProjectChecklistTemplateOpen =
    idProjectChecklistTemplateOpen !== '' &&
    idProjectChecklistTemplateOpen !== '-1';
  const isProjectSaveChecklistAsTemplateOpen =
    idProjectSaveChecklistAsTemplateOpen !== '' &&
    idProjectSaveChecklistAsTemplateOpen !== '-1' &&
    idProjectSaveChecklistAsTemplateOpen !== '-2';
  const isFromProjectChecklist =
    isProjectChecklistTemplateOpen || isProjectSaveChecklistAsTemplateOpen;
  const noPadding = isFromProjectChecklist || !!idTaskDetailOpen;

  // Check if current route is the 'files' tab in project details
  // Fallback to pathname check if navigation state is not available
  const path =
    globalThis.window === undefined ? '' : globalThis.location.pathname;
  const pathnameCheck =
    /^\/?projects\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/files\/?$/.test(
      path
    );

  const isProjectFilesRoute = currentRouteName === 'files' || pathnameCheck;

  return (
    <Box flex={chatExpanded ? 0 : 1} flexDirection='column'>
      <DropZone onDrop={isProjectFilesRoute ? handleDrop : undefined}>
        {({ Input, isDragActive }) => (
          <>
            <Input />
            {children}
            {/* Drag Overlay - shown when dragging files */}
            {isDragActive && onDropFile && isProjectFilesRoute && (
              <Box
                position='absolute'
                top={0}
                left={0}
                right={0}
                bottom={0}
                backgroundColor='greenSecondaryMild'
                opacity={0.95}
                justifyContent='center'
                alignItems='center'
                zIndex={1000}>
                <Box
                  bg='white'
                  borderRadius='l'
                  px='m'
                  py='s'
                  gap='s'
                  alignItems='center'
                  justifyContent='center'
                  flexDirection='row'>
                  <Icon name='UploadCloud' />
                  <Text
                    variant='bodyTaskName'
                    color='textPrimary'
                    textAlign='center'
                    fontWeight='600'>
                    Attach file
                  </Text>
                </Box>
              </Box>
            )}
          </>
        )}
      </DropZone>
      {/* TA-10891: checklist-template and task panels (new task, task detail,
          edit task) slide in, and slide back out before unmounting. */}
      <SlidePanelPresence open={!chatExpanded && isFromProjectChecklist}>
        {isFromProjectChecklist && panelContent(false)}
      </SlidePanelPresence>
      {/* Paywall subscription flow: trial confirmation modal */}
      {showPaywallSubscriptionModal && (
        <TrialConfirmModal
          visible={showPaywallSubscriptionModal}
          onClose={() => {
            setUpgradeTeamPlanOpen(false);
            setShowPaywallSubscriptionModal(false);
          }}
          onStartTrial={handlePaywallStartTrial}
          team={paywallTeam ?? team}
          teamName={
            (paywallTeam ?? team)?.name ??
            (teamValues as FormValues | undefined)?.name
          }
          memberCount={paywallSeatCount}
        />
      )}
      {/* Regular side panel flows (non-paywall) */}
      {!chatExpanded &&
        isUpgradeTeamPlanOpen &&
        !showPaywallSubscriptionModal &&
        upgradeCTA && (
          <NavigationContainer independent={true}>
            <Pressable
              onPress={() => {}}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                backgroundColor: theme.colors.grey06,
                zIndex: 1000,
              }}>
              <Box
                style={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </Pressable>
            <Box
              position='absolute'
              left={0}
              right={0}
              bottom={0}
              top={0}
              backgroundColor='white'
              style={{ zIndex: 1001 }}>
              <CreateSubscriptionStack
                externalNavigate={navigation}
                params={{
                  teamId: idTeamOpen,
                  teamUUId: uuidTeamOpen,
                  teamValues: teamValues,
                  isPersonal: isTeamPersonal,
                  isPlanPlus: false,
                  screenName: 'create-subscriptionPanel',
                }}
              />
            </Box>
          </NavigationContainer>
        )}
      {!chatExpanded &&
        isUpgradeTeamPlanOpen &&
        !showPaywallSubscriptionModal &&
        !upgradeCTA && (
          <NavigationContainer independent={true}>
            <Box
              position='absolute'
              right={0}
              bottom={0}
              top={0}
              width={580}
              borderWidth={1}
              borderColor='grey02'
              backgroundColor='white'
              style={{ zIndex: 1001 }}>
              <CreateSubscriptionStack
                externalNavigate={navigation}
                params={{
                  teamId: idTeamOpen,
                  teamUUId: uuidTeamOpen,
                  teamValues: teamValues,
                  isPersonal: isTeamPersonal,
                  isPlanPlus: false,
                  screenName: 'create-subscriptionPanel',
                }}
              />
            </Box>
          </NavigationContainer>
        )}
      {/* panelContent renders the checklist template whenever one is open, so
          yield to the checklist presence above instead of stacking a second
          copy of it on top (TA-10891 QA double image). */}
      <SlidePanelPresence
        open={!chatExpanded && isTaskWebPanelOpen && !isFromProjectChecklist}>
        {isTaskWebPanelOpen && !isFromProjectChecklist && panelContent(false)}
      </SlidePanelPresence>

      {!chatExpanded && isContactWebPanelOpen && (
        <NavigationContainer independent={true}>
          <Box
            style={{
              borderColor: theme.colors.black,
            }}
            position='absolute'
            right={0}
            bottom={0}
            top={0}
            width={isTaskWebPanelMaximize ? mainPanelWidth : 580}
            borderColor='grey02'>
            {isContactWebPanelOpen && (
              <CreateContactStack contactGroupId={idContactDetailOpen} />
            )}
          </Box>
        </NavigationContainer>
      )}

      <SlidePanelPresence open={isCreateProjectOpen}>
        {isCreateProjectOpen &&
          (() => {
            const createProjectContent = (
              <>
                <Box flexDirection='row' marginBottom='l'>
                  <Box flex={1}>
                    <Text variant='heading2'>
                      {editProjectUUId
                        ? t('models:projects.edit.title')
                        : t('models:projects.create.title')}
                    </Text>
                  </Box>
                  <Box
                    height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                    alignItems='center'
                    justifyContent='center'>
                    <WebIcon
                      accessibilityLabel='Close'
                      name='X'
                      variant='m'
                      onPress={handleCreateProjectHeaderClose}
                      color='textPrimary'
                    />
                  </Box>
                </Box>
                <CreateProject
                  externalNavigate={navigation}
                  attemptCloseRef={createProjectAttemptCloseRef}
                />
              </>
            );
            const sharedBoxProps = {
              bottom: 0,
              top: 0,
              width: 578,
              borderWidth: 1,
              px: 'l',
              pt: 'l',
              pb: 'l',
              borderColor: 'grey02',
              backgroundColor: 'white',
            } as const;
            return (
              <NavigationContainer independent={true}>
                {showOverlay ? (
                  <Overlay
                    visible={showOverlay}
                    dismissable={false}
                    animationType='slide-panel'>
                    <SlidePanelBox
                      position='absolute'
                      right={
                        !chatExpanded && !(isOpenChat || chatListOpen)
                          ? 80
                          : chatWidth
                      }
                      {...sharedBoxProps}>
                      {createProjectContent}
                    </SlidePanelBox>
                  </Overlay>
                ) : (
                  <>
                    {/* Non-dismissing backdrop so an accidental click can't drop the unsaved form. */}
                    <SlideBackdropBox
                      position='absolute'
                      top={0}
                      bottom={0}
                      left={-leftPartWidth}
                      right={0}
                    />
                    <SlidePanelBox
                      position='absolute'
                      right={0}
                      {...sharedBoxProps}>
                      {createProjectContent}
                    </SlidePanelBox>
                  </>
                )}
              </NavigationContainer>
            );
          })()}
      </SlidePanelPresence>
      {isEditProfileOpen && (
        <NavigationContainer
          documentTitle={{ enabled: false }}
          independent={true}>
          <Box
            position='absolute'
            right={0}
            bottom={0}
            top={0}
            width={578}
            borderWidth={1}
            px='l'
            pb='l'
            borderColor='grey02'
            backgroundColor='white'>
            <Box flexDirection='row' marginTop='m'>
              <Box flex={1}>
                <Text variant='heading2'>Edit My Account</Text>
              </Box>
              <Box
                width={20}
                height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                alignItems='center'
                justifyContent='center'>
                <Icon
                  accessibilityLabel='Close'
                  name='X'
                  variant='m'
                  onPress={() => {
                    setIsEditProfileOpen(false);
                  }}
                  color='textPrimary'
                />
              </Box>
            </Box>
            <NewEditProfile />
          </Box>
        </NavigationContainer>
      )}
      {isBottomBannerShow && (
        <ChatEmptyState
          onClose={() => {
            setIsBottomBannerShow(false);
          }}
          onContentPress={() => {
            setIsModalShow(true);
            setIsBottomBannerShow(false);
          }}
          type={EmptyStateType.Banner}></ChatEmptyState>
      )}
      {isModalShow && (
        <WebModal
          onClose={() => {
            setIsModalShow(false);
            setIsBottomBannerShow(true);
          }}
          visible={isModalShow}
          width={400}
          height={413}>
          <ChatEmptyState
            onClose={() => {
              setIsModalShow(false);
              setIsBottomBannerShow(true);
            }}
            type={EmptyStateType.Popup}></ChatEmptyState>
        </WebModal>
      )}
      {subscriptionCreatedModalOpen && (
        <SubscriptionCreatedModal
          teamUUId={subscriptionCreatedTeamUUId}
          isFromCreated={true}
          onClose={() => {
            setSubscriptionCreatedModalOpen(false);
          }}
        />
      )}
      {upgradeConfirmModalOpen && (
        <UpgradeConfirmModal
          visible={upgradeConfirmModalOpen}
          onClose={() => {
            if (continuingToCheckoutRef.current) {
              continuingToCheckoutRef.current = false;
              return;
            }
            setUpgradeConfirmModalOpen(false);
          }}
          onContinueToCheckout={handleContinueToCheckout}
          team={upgradeConfirmTeam}
          teamName={upgradeConfirmTeamName}
          memberCount={upgradeConfirmMemberCount}
        />
      )}

      {editTeamConfirmModalOpen && (
        <EditTeamConfirmModal
          visible={editTeamConfirmModalOpen}
          onClose={() => setEditTeamConfirmModalOpen(false)}
          onConfirm={handleConfirmEditTeam}
          team={editTeamConfirmTeam}
          teamName={editTeamConfirmTeam?.name}
        />
      )}

      {paywallModalOpen && (
        <TeamPaywallModal
          visible={paywallModalOpen}
          onClose={() => {
            setPaywallModalOpen(false);
            setPaywallTeam(undefined);
          }}
          team={paywallTeam}
        />
      )}

      {showPaywallTeamSetup && (
        <Formik
          initialValues={teamSetupInitialValues}
          validationSchema={createTeamSchemaPaywall}
          onSubmit={(values, { setSubmitting }) => {
            submitPaywallTeamSetup(values, setSubmitting);
          }}>
          {() => (
            <TeamsModal
              title='Team Setup'
              buttonLabel={t('shared:next')}
              onClose={() => setShowPaywallTeamSetup(false)}
            />
          )}
        </Formik>
      )}

      {showPaywallInviteMembers && (
        <InviteMemberModal
          isVisible={showPaywallInviteMembers}
          teamId={uuidTeamOpen || paywallTeam?.uuid || ''}
          onClose={handlePaywallInviteDone}
          onMembersAdded={handlePaywallInviteDone}
          requireMemberSelection={false}
          // No team yet in the deferred-creation path — collect invitations
          // locally and send them as teamAttributes.teamMembers on subscribe
          onDeferredSend={
            uuidTeamOpen || paywallTeam?.uuid
              ? undefined
              : setPaywallPendingMembers
          }
        />
      )}
      {currentProject && !isMobileBrowser && (
        <Animated.View
          style={{
            position: 'absolute',
            bottom: theme.spacing.m,
            right: theme.spacing.m,
            width: Math.min(
              440,
              Dimensions.get('window').width - theme.spacing.l * 2
            ),
            opacity: cardFadeAnim,
            zIndex: 500,
          }}>
          {behindCount > 0 && (
            <Box
              flexDirection='row'
              justifyContent='flex-end'
              marginBottom='xs'>
              {/* Labelled, not a bare X: this clears the whole queue and there
                  is no undo, and the card's own dismiss is an unlabelled grey X
                  ~24px below it. Two anonymous X glyphs one above the other made
                  "close this card" and "dismiss every card" indistinguishable,
                  with the destructive one on top. */}
              <Pressable
                testID='projects.onboardingCard.dismissAll'
                accessibilityRole='button'
                accessibilityLabel={t(
                  'models:projects.onboardingCard.dismissAll'
                )}
                onPress={() => fadeOutThen(dismissAll)}>
                <Box
                  backgroundColor='white'
                  borderRadius='s'
                  paddingHorizontal='m'
                  paddingVertical='xxs'
                  style={onboardingChipShadow}>
                  <Text variant='webBodyHintSubline' color='grey04'>
                    {t('models:projects.onboardingCard.dismissAll')}
                  </Text>
                </Box>
              </Pressable>
            </Box>
          )}
          <Box>
            {/* Slivers of the queued cards, peeking out under the top one. */}
            {visibleProjects.slice(1).map((project, index) => (
              <Box
                key={project.id}
                backgroundColor='white'
                borderRadius='m'
                borderWidth={1}
                borderColor='grey02'
                style={onboardingLayerStyle(index)}
              />
            ))}
            {/* Not wrapped in a ScrollView: it clips overflowX, which cuts the
                card's shadow into grey squares at the rounded corners. */}
            <Box style={{ zIndex: 3 }}>
              <ProjectOnboardingCard
                projectId={currentProject.id}
                projectName={currentProject.name}
                onDismiss={() => {
                  // Only fade the container out when this is the last card —
                  // otherwise the cards left behind would fade with it.
                  if (behindCount === 0) {
                    fadeOutThen(() => dismissProject(currentProject.id));
                  } else {
                    dismissProject(currentProject.id);
                  }
                }}
                onGoToProject={() => {
                  fadeOutThen(() => {
                    // Evict the project from the Apollo cache so ProjectDetailTabs
                    // is forced to re-fetch with the user's updated membership status.
                    // Without this, a user who was a non-member before being added
                    // in real-time would see a stale non-member view (or crash if the
                    // tab params hadn't propagated yet).
                    const cacheId = apolloClient.cache.identify({
                      __typename: 'Project',
                      id: currentProject.id,
                    });
                    if (cacheId) {
                      apolloClient.cache.evict({ id: cacheId });
                      apolloClient.cache.gc();
                    }
                    navigation.navigateToProject(currentProject);
                    goToProject(currentProject);
                  });
                }}
              />
            </Box>
          </Box>
          {behindCount > 0 && (
            <Box
              alignSelf='center'
              backgroundColor='white'
              borderRadius='s'
              // 24px of clear space either side of the label so it doesn't run
              // into the list rows it floats over.
              paddingHorizontal='l'
              paddingVertical='xxs'
              style={{
                ...onboardingChipShadow,
                // Has to clear the slivers, which hang below the card.
                marginTop:
                  visibleProjects.slice(1).length * ONBOARDING_STACK_PEEK +
                  theme.spacing.s,
              }}>
              <Text variant='webBodyHintSubline' color='grey04'>
                {t('models:projects.onboardingCard.moreBehind', {
                  n: behindCount,
                })}
              </Text>
            </Box>
          )}
        </Animated.View>
      )}
    </Box>
  );
};
